import {
  BroadcastManyQuery,
  EnumBroadcastResourcesType,
  EnumOwnerType,
  Multilingual,
  Owner,
  useCourseProgressGetQuery,
} from "gql/generated";
import { Flex } from "styled-system/jsx";
import { HeaderImage } from "./HeaderImage";
import {
  getMultilingualString,
  isLandscape,
  downloadAssetsAsZip,
  isZipDownloadDisabledForAssets,
} from "shared/utils";
import { Text, Heading, Button, Icon, Modal, Tag } from "@ttc3k/trekker";
import { CheckCircleSolid, Clock, XmarkCircle } from "iconoir-react";
import { DashedCircle } from "shared/components/atoms";
import { ScormPlayer } from "shared/components/organisms/ScormPlayer";
import { useTranslation } from "react-i18next";
import {
  BroadcastStatusIndicator,
  CloudinaryImage,
  HtmlContent,
} from "shared/components";
import { SmartImage } from "shared/components/atoms";
import type { SurveyFragmentFragment } from "gql/generated";
import { BroadcastFormInline } from "./BroadcastFormInline";
import { CustomResourceIframeModal } from "./CustomResourceIframeModal";
import { MultiSubmissionIndex } from "./MultiSubmissionIndex";
import { getCloudinary } from "shared/cloudinary";
import { useState, useCallback, useEffect } from "react";
import { fit } from "@cloudinary/transformation-builder-sdk/actions/resize";
import { useBroadcastPostHogEvents } from "../hooks/useBroadcastPostHogEvents";
import { BroadcastStatus } from "apps/home/constants";
import { usePostHog } from "shared/hooks";
import { i18nLanguage } from "i18n";
import { StorefrontOrdersIndex, type DefaultContact } from "./StorefrontOrdersIndex";
import type { UserOperator } from "widgets/booker/contexts/BookerContextProvider/BookerContext";

type ViewResourceProps = {
  resource: NonNullable<BroadcastManyQuery["broadcastMany"][0]["resources"][0]>;
  organizationName?: Multilingual;
  personId?: string;
  personName?: string;
  isPreview: boolean;
  broadcastId: string;
  broadcastOwner?: Owner;
  broadcastName?: Multilingual;
  status: BroadcastStatus;
  submittedCount?: number;
  pendingCount?: number;
  draftCount?: number;
  rejectedCount?: number;
  onDirtyChange?: (dirty: boolean) => void;
  defaultContact?: DefaultContact;
  userOperators?: UserOperator[];
  isAdmin?: boolean;
};

export const ViewResource = ({
  resource,
  organizationName,
  personId,
  isPreview,
  broadcastId,
  status,
  submittedCount,
  pendingCount,
  draftCount,
  rejectedCount,
  personName,
  broadcastOwner,
  broadcastName,
  onDirtyChange,
  defaultContact,
  userOperators,
  isAdmin,
}: ViewResourceProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST",
  });
  const { trackResourceViewedEvent } = useBroadcastPostHogEvents(
    personId,
    broadcastId,
    personName,
    broadcastOwner,
    broadcastName,
  );

  // Fetch course progress for status badge (only for COURSE type)
  const { data: courseProgressData, refetch: refetchParentCourseProgress } =
    useCourseProgressGetQuery({
      variables: {
        resourceId: resource._id,
        broadcastId: broadcastId,
      },
      skip:
        isPreview ||
        !resource._id ||
        !broadcastId ||
        resource.type !== EnumBroadcastResourcesType.Course,
    });

  useEffect(() => {
    if (!isPreview) {
      trackResourceViewedEvent(resource._id, resource.title, resource.type);
    }
  }, [
    trackResourceViewedEvent,
    resource._id,
    isPreview,
    broadcastId,
    resource.title,
    resource.type,
  ]);

  // Determine badge for courses
  const hasProgress = !!courseProgressData?.courseProgressGet;
  const isCompleted =
    courseProgressData?.courseProgressGet?.completionStatus === "COMPLETED";
  const showCourseBadge =
    resource.type === EnumBroadcastResourcesType.Course && hasProgress;

  return (
    <Flex flexDir={"column"} gap={"400"}>
      <Flex flexDir={"column"} gap={"300"}>
        <Flex flexDir={"column"} gap={"150"}>
          <Flex flexDir={"column"} gap={"50"}>
            <Text visual={"smallTag"} color={"text.light"}>
              {getMultilingualString(organizationName ?? {})}
            </Text>
            <Heading size={"h4"} color={"text.dark"}>
              {getMultilingualString(resource.title)}
            </Heading>
          </Flex>
          <Flex alignItems={"center"} gap={"100"} flexWrap="wrap">
            <Tag mode={"light"}>{t(`RESOURCE_TYPE.${resource.type}`)}</Tag>
            {showCourseBadge ? (
              <BroadcastStatusIndicator
                status={isCompleted ? "COMPLETED" : "IN_PROGRESS"}
              />
            ) : submittedCount !== undefined ? (
              <Flex alignItems="center" gap="150">
                {(submittedCount ?? 0) > 0 && (
                  <Flex alignItems="center" gap="75">
                    <Text visual="smallSemiBold" color="text.success.mid">
                      {t("FORM_MULTI_SUBMITTED_COUNT", { count: submittedCount })}
                    </Text>
                    <Icon Element={CheckCircleSolid} color="icon.success.mid" size="sm" />
                  </Flex>
                )}
                {(pendingCount ?? 0) > 0 && (
                  <Flex alignItems="center" gap="75">
                    <Text visual="smallSemiBold" color="text.warning.mid">
                      {t("FORM_MULTI_PENDING_COUNT", { count: pendingCount })}
                    </Text>
                    <Icon Element={Clock} color="icon.warning.mid" size="sm" />
                  </Flex>
                )}
                {(rejectedCount ?? 0) > 0 && (
                  <Flex alignItems="center" gap="75">
                    <Text visual="smallSemiBold" color="text.error.mid">
                      {t("FORM_MULTI_REJECTED_COUNT", { count: rejectedCount })}
                    </Text>
                    <Icon Element={XmarkCircle} color="icon.error.mid" size="sm" />
                  </Flex>
                )}
                {(draftCount ?? 0) > 0 && (
                  <Flex alignItems="center" gap="75">
                    <Text visual="smallSemiBold" color="text.highlight.mid">
                      {t("FORM_MULTI_DRAFT_COUNT", { count: draftCount })}
                    </Text>
                    <DashedCircle color="icon.highlight.light" width="16px" height="16px" />
                  </Flex>
                )}
                {(submittedCount ?? 0) === 0 && (pendingCount ?? 0) === 0 && (rejectedCount ?? 0) === 0 && (draftCount ?? 0) === 0 && (
                  <BroadcastStatusIndicator status={status} />
                )}
              </Flex>
            ) : (
              <BroadcastStatusIndicator status={status} />
            )}
          </Flex>
        </Flex>

        <HeaderImage
          type={"DEFAULT"}
          image={resource.coverImage}
          doNotShowFallback
          smartFill
        />

        <ResourceContent
          resource={resource}
          personId={personId}
          isPreview={isPreview}
          broadcastId={broadcastId}
          refetchParentCourseProgress={refetchParentCourseProgress}
          broadcastOwner={broadcastOwner}
          personName={personName}
          broadcastName={broadcastName}
          onDirtyChange={onDirtyChange}
          defaultContact={defaultContact}
          userOperators={userOperators}
          isAdmin={isAdmin}
        />
      </Flex>
    </Flex>
  );
};

type ResourceContentProps = {
  resource: NonNullable<BroadcastManyQuery["broadcastMany"][0]["resources"][0]>;
  personId?: string;
  isPreview: boolean;
  broadcastId: string;
  refetchParentCourseProgress: () => void;
  personName?: string;
  broadcastOwner?: Owner;
  broadcastName?: Multilingual;
  onDirtyChange?: (dirty: boolean) => void;
  defaultContact?: DefaultContact;
  userOperators?: UserOperator[];
  isAdmin?: boolean;
};

const ResourceContent = ({
  resource,
  personId,
  isPreview,
  broadcastId,
  refetchParentCourseProgress,
  personName,
  broadcastOwner,
  broadcastName,
  onDirtyChange,
  defaultContact,
  userOperators,
  isAdmin,
}: ResourceContentProps) => {
  const [isModalOpen, setIsModalOpen] = useState({
    videoModal: false,
    courseModal: false,
    customModal: false,
  });

  const { t, i18n } = useTranslation("home", {
    keyPrefix: "VIEWS.BROADCAST.VIEW_MODAL",
  });
  const { trackPostHogException } = usePostHog();

  const {
    trackResourceDownloadedEvent,
    trackResourcePlayedEvent,
    trackResourceInProgressEvent,
    trackResourceCompletedEvent,
  } = useBroadcastPostHogEvents(
    personId,
    broadcastId,
    personName,
    broadcastOwner,
    broadcastName,
  );

  // Fetch course progress for all resources (only runs for COURSE type)
  const { data: courseProgressData, refetch: refetchCourseProgress } =
    useCourseProgressGetQuery({
      variables: {
        resourceId: resource._id,
        broadcastId: broadcastId,
      },
      skip:
        isPreview ||
        !resource._id ||
        !broadcastId ||
        resource.type !== EnumBroadcastResourcesType.Course,
      onCompleted: data => {
        if (data?.courseProgressGet?.completionStatus === "COMPLETED") {
          trackResourceCompletedEvent(resource._id);
        }
      },
    });

  const [isDownloading, setIsDownloading] = useState(false);
  const photosZipDisabled = isZipDownloadDisabledForAssets(
    resource.photos ?? [],
  );
  const handleDownloadPhotos = useCallback(async () => {
    if (!resource.photos || resource.photos.length === 0) return;
    if (isZipDownloadDisabledForAssets(resource.photos)) return;

    setIsDownloading(true);
    await downloadAssetsAsZip(
      resource.photos,
      "photos",
      `photos_${Date.now()}.zip`,
      trackPostHogException,
    );
    setIsDownloading(false);
    if (!isPreview) {
      trackResourceDownloadedEvent(resource._id, resource.type);
    }
  }, [
    resource.photos,
    isPreview,
    trackResourceDownloadedEvent,
    resource._id,
    trackPostHogException,
    resource.type,
  ]);

  switch (resource.type) {
    case EnumBroadcastResourcesType.Message: {
      const messageHtml = getMultilingualString(resource.message ?? {});
      return (
        <>
          {resource.description && (
            <Text visual={"text.mid"}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
          {messageHtml.length > 0 ? <HtmlContent html={messageHtml} /> : null}
        </>
      );
    }
    case EnumBroadcastResourcesType.Pdf: {
      const pdfUrl = getCloudinary(resource.file?._id).image(resource.file?._id).toURL();

      const handleOnClick = () => {
        if (!isPreview) {
          trackResourceDownloadedEvent(resource._id, resource.type);
        }
      };

      return (
        <>
          <Button
            as={"a"}
            href={pdfUrl}
            target="_blank"
            visual={"accent.blue"}
            onClick={handleOnClick}
          >
            {t("DOWNLOAD_FILE")}
          </Button>
          {resource.description && (
            <Text visual={"text.mid"}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
        </>
      );
    }
    case EnumBroadcastResourcesType.Video: {
      const handleWatchVideoClick = () => {
        if (!isPreview) {
          trackResourcePlayedEvent(resource._id);
        }
        if (resource.videoURL && resource.videoURL.length > 0) {
          window.open(resource.videoURL, "_blank");
        } else {
          setIsModalOpen(prev => ({ ...prev, videoModal: true }));
        }
      };
      return (
        <>
          <Button visual={"accent.blue"} onClick={handleWatchVideoClick}>
            {t("WATCH_VIDEO")}
          </Button>
          {resource.description && (
            <Text visual={"text.mid"}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
          <Modal
            open={isModalOpen.videoModal}
            onOpenChange={() =>
              setIsModalOpen(prev => ({ ...prev, videoModal: false }))
            }
            placement={"centerPane"}
            closeTriggerVisual={"light"}
            portal
          >
            <CloudinaryImage
              image={resource.video}
              videoSpecificProps={{
                showControls: true,
                autoPlay: false,
                loop: false,
                muted: false,
                playsInline: true,
              }}
              imageStyleProps={{
                maxH: "90vh",
                width: "full",
                maxW: "min(100vw, 700px)",
                aspectRatio: "unset!",
              }}
              resizeVideo={
                isLandscape(resource.video)
                  ? fit().width(700)
                  : fit().height(600)
              }
              previewVideo={false}
            />
          </Modal>
        </>
      );
    }
    case EnumBroadcastResourcesType.Photos: {
      return (
        <>
          <Button
            visual={"accent.blue"}
            onClick={handleDownloadPhotos}
            isLoading={isDownloading}
            disabled={photosZipDisabled}
            title={
              photosZipDisabled
                ? t("core:MEDIA.ICONS.DOWNLOAD_UNAVAILABLE")
                : undefined
            }
          >
            {t("DOWNLOAD_PHOTOS")}
          </Button>
          {resource.description && (
            <Text visual={"text.mid"}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
          <Flex
            alignItems={"flex-start"}
            alignContent={"flex-start"}
            gap={"250"}
            flexWrap={"wrap"}
            width={"full"}
          >
            {resource.photos.map(photo => (
              <SmartImage
                key={photo._id}
                image={photo}
                width={200}
                shape="rounded"
              />
            ))}
          </Flex>
        </>
      );
    }
    case EnumBroadcastResourcesType.Forms: {
      const survey = resource.survey as SurveyFragmentFragment | undefined;
      const formMode = resource.formMode ?? "ONE_AND_DONE";
      const isSharedForm = formMode === "COLLABORATIVE";
      const resAllowSave = isSharedForm || (resource.allowSaveAndEdit ?? undefined);
      const resAllowResubmission = resource.allowResubmission ?? undefined;

      return (
        <>
          {resource.description && (
            <Text visual={"text.mid"}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
          {survey && formMode === "MULTI_SUBMISSION" && (
            <MultiSubmissionIndex
              survey={survey}
              isPreview={isPreview}
              broadcastId={broadcastId}
              resourceId={resource._id}
              personId={personId}
              personName={personName}
              broadcastName={broadcastName}
              broadcastOwner={broadcastOwner}
              allowSaveAndEdit={resAllowSave}
              allowResubmission={resAllowResubmission}
              onDirtyChange={onDirtyChange}
              teamVisible={resource.teamVisible ?? false}
              userOperators={userOperators}
              isAdmin={isAdmin}
              ctaLabel={resource.ctaLabel}
              broadcastOwnerOrgId={
                broadcastOwner?.type === EnumOwnerType.Organization
                  ? broadcastOwner.entityId
                  : undefined
              }
            />
          )}
          {survey && formMode !== "MULTI_SUBMISSION" && (
            <BroadcastFormInline
              survey={survey}
              isPreview={isPreview}
              broadcastId={broadcastId}
              resourceId={resource._id}
              personId={personId}
              personName={personName}
              broadcastName={broadcastName}
              broadcastOwner={broadcastOwner}
              allowSaveAndEdit={resAllowSave}
              isSharedForm={isSharedForm}
              allowResubmission={resAllowResubmission}
              onDirtyChange={onDirtyChange}
              userOperators={isAdmin ? undefined : userOperators}
            />
          )}
        </>
      );
    }
    case EnumBroadcastResourcesType.Course: {
      const handleStartCourse = () => {
        if (!isPreview) {
          trackResourceInProgressEvent(resource._id);
        }
        setIsModalOpen(prev => ({ ...prev, courseModal: true }));
      };

      const handleCloseCourseModal = () => {
        setIsModalOpen(prev => ({ ...prev, courseModal: false }));
        // Refetch course progress to update badges in both parent and child
        refetchCourseProgress();
        refetchParentCourseProgress();
      };

      const hasProgress = !!courseProgressData?.courseProgressGet;
      const completionStatus =
        courseProgressData?.courseProgressGet?.completionStatus;
      const isCompleted = completionStatus === "COMPLETED";

      return (
        <>
          <Button
            visual={"accent.blue"}
            onClick={handleStartCourse}
            disabled={!resource.course}
          >
            {isCompleted
              ? t("RESTART_COURSE")
              : hasProgress
                ? t("RESUME_COURSE")
                : t("START_COURSE")}
          </Button>
          {resource.description && (
            <Text visual={"text.mid"}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
          <Modal
            open={isModalOpen.courseModal}
            onOpenChange={handleCloseCourseModal}
            placement={"centerPane"}
            closeTriggerVisual={"light"}
            noMaxH // Removes second scrollbar since the ScormPlayer has it's own scrollbar
            portal
          >
            <Flex
              width={"calc(100vw - 64px)"}
              height={"calc(100vh - 64px)"}
              borderRadius={"200"}
              overflow={"hidden"}
              bg={"white"}
            >
              {resource.course && (
                <ScormPlayer
                  courseUrl={resource.course}
                  courseTitle={getMultilingualString(resource.title)}
                  resourceId={resource._id}
                  broadcastId={broadcastId}
                  isPreview={isPreview}
                />
              )}
            </Flex>
          </Modal>
        </>
      );
    }
    case EnumBroadcastResourcesType.Storefront: {
      const bookerId = (resource as any).booker?._id;
      return (
        <>
          {resource.description && (
            <Text visual={"text.mid"}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
          {bookerId && (
            <StorefrontOrdersIndex
              bookerId={bookerId}
              broadcastId={broadcastId}
              resourceId={resource._id}
              personId={personId}
              isPreview={isPreview}
              language={i18n.language as i18nLanguage}
              defaultContact={defaultContact}
              userOperators={userOperators}
              organizationId={
                broadcastOwner?.type === EnumOwnerType.Organization
                  ? broadcastOwner.entityId
                  : undefined
              }
              ctaLabel={
                resource.ctaLabel
                  ? getMultilingualString(resource.ctaLabel) || undefined
                  : undefined
              }
            />
          )}
        </>
      );
    }
    default:
      if (resource.type === EnumBroadcastResourcesType.Custom) {
        const handleOpenCustom = () => {
          setIsModalOpen(prev => ({ ...prev, customModal: true }));
        };
        const handleCloseCustom = () => {
          setIsModalOpen(prev => ({ ...prev, customModal: false }));
        };
        return (
          <>
            <Button
              visual={"accent.blue"}
              onClick={handleOpenCustom}
              disabled={!resource.iframeUrl}
            >
              {t("OPEN_RESOURCE")}
            </Button>
            {resource.description && (
              <Text visual={"text.mid"}>
                {getMultilingualString(resource.description)}
              </Text>
            )}
            {!!resource.iframeUrl && (
              <CustomResourceIframeModal
                open={isModalOpen.customModal}
                onClose={handleCloseCustom}
                iframeUrl={resource.iframeUrl}
              />
            )}
          </>
        );
      }
      return (
        <>
          {resource.description && (
            <Text visual={"text.mid"}>
              {getMultilingualString(resource.description)}
            </Text>
          )}
        </>
      );
  }
};
